import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useTranslation, Trans } from 'react-i18next';
import { AuthHeader } from "@/components/AuthHeader";

const createFormSchema = (t: (key: string) => string) => {
  return z.object({
    email: z.string().email({ message: t('validation.invalidEmail') }),
    password: z.string().min(6, {
      message: t('validation.passwordMinLength'),
    }),
  });
};

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

export default function Login() {
  const { t, i18n } = useTranslation();
  const { user, login, loginWithGoogle, loading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoggingInWithGoogle, setIsLoggingInWithGoogle] = useState(false);
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';

  const form = useForm<FormData>({
    resolver: zodResolver(createFormSchema(t)),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: FormData) => {
    try {
      setError(null);
      await login(values.email, values.password);
    } catch (err) {
      console.error("Login error:", err);
      setError(t('auth.errors.invalidCredentials'));
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setIsLoggingInWithGoogle(true);
      setError(null);
      await loginWithGoogle();
    } catch (err) {
      console.error("Google login error:", err);
      setError(t('auth.errors.googleLoginFailed'));
    } finally {
      setIsLoggingInWithGoogle(false);
    }
  };

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <AuthHeader />
      <div className="min-h-screen flex flex-col sm:flex-row">
        <div className="hidden sm:flex sm:flex-1 bg-primary items-center justify-center">
          <div className="p-8 max-w-md text-primary-foreground animate-fade-in">
            <h2 className="text-2xl font-bold mb-6">{t('auth.login.title')}</h2>
            <p className="text-primary-foreground/80 mb-8">
              {t('auth.login.subtitle')}
            </p>
            <div className="space-y-6">
              {[
                t('auth.login.features.manageConnections'),
                t('auth.login.features.monitorStatus'),
                t('auth.login.features.secureInterface'),
              ].map((feature, i) => (
                <div key={i} className="flex items-center">
                  <div className="h-6 w-6 rounded-full bg-primary-foreground/20 flex items-center justify-center mr-3">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      className="h-3 w-3"
                    >
                      <path
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                  </div>
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex-1 flex flex-col justify-center px-6 py-12 lg:px-8 bg-background animate-fade-in pt-16">
          <div className="sm:mx-auto sm:w-full sm:max-w-sm">
            <h2 className="mt-6 text-center text-2xl font-bold leading-9 tracking-tight">
              {t('auth.login.title')}
            </h2>
          </div>

          <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-sm">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-6">
              <div>
                <Button
                  variant="outline"
                  type="button"
                  className="w-full"
                  onClick={handleGoogleLogin}
                  disabled={isLoggingInWithGoogle || loading}
                >
                  {isLoggingInWithGoogle ? (
                    <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <svg className={`animate-spin h-4 w-4 text-gray-500 ${isRTL ? 'ml-2' : 'mr-2'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('auth.login.signingIn')}
                    </span>
                  ) : (
                    <span className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <svg className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      {t('auth.login.continueWithGoogle')}
                    </span>
                  )}
                </Button>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    {t('auth.login.orContinueWith')}
                  </span>
                </div>
              </div>
            </div>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.fields.email')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('common.placeholders.email')}
                          type="email"
                          autoComplete="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>{t('common.fields.password')}</FormLabel>
                        <Link
                          to="/forgot-password"
                          className="text-sm font-medium text-primary hover:underline"
                        >
                          {t('auth.login.forgotPassword')}
                        </Link>
                      </div>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="current-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="text-xs text-muted-foreground mt-4">
                  <p className="mb-2">
                    <Trans
                      i18nKey="auth.login.termsAndConditions"
                      components={[
                        <Link to="/terms" className="font-medium text-primary hover:text-primary/90" />,
                        <Link to="/privacy" className="font-medium text-primary hover:text-primary/90" />,
                      ]}
                    />
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? t('auth.login.loggingIn') : t('auth.login.loginButton')}
                </Button>
              </form>
            </Form>

            <p className="mt-10 text-center text-sm text-muted-foreground">
              {t('auth.login.noAccount')}{" "}
              <Link to="/signup" className="font-semibold text-primary hover:text-primary/90">
                {t('auth.signup.label')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
